import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { cn } from "cn";
import { Check, ChevronRight, Circle } from "lucide-react";
import type * as React from "react";

// v3.2 menus (redesign-v3.2/assets/style.css .menu) on Radix DropdownMenu,
// like the rest of the web app's popovers, so a menu inside a Sheet shares
// one dismiss layer: the panel colour, a soft pop shadow, 44px items on
// touch screens and 36px from 1024px.
//
// The class strings are exported too: apps/web's shared shadcn menu and
// select add the *_PANEL/_ROW ones under the v3.2 flag, and
// components/ui/v3/menu.ts re-exports the rest.

/** The panel of any v3.2 popover menu or select list. */
export const MENU_PANEL =
  "v3-remap rounded-xl border-0 bg-v3-panel font-v3 text-v3-text shadow-v3-pop";
/** A row of a menu or select list (focus and open states). */
export const MENU_ROW =
  "rounded-lg text-[14px] text-v3-text focus:bg-v3-surface-2 focus:text-v3-text data-[state=open]:bg-v3-surface-2";
export const MENU_LABEL_TEXT = "text-[12px] font-medium text-v3-text-3";
export const MENU_RULE = "bg-v3-border";

/** A menu's content: at least 224px wide, 4px inset. */
export const MENU_CONTENT =
  "min-w-56 rounded-xl border-0 bg-v3-panel p-1 font-v3 text-v3-text shadow-v3-pop";
/** A menu item: 44px on touch screens, 36px from 1024px; 16px icons. */
export const MENU_ITEM =
  "min-h-11 gap-3 rounded-lg px-3 text-[14px] text-v3-text focus:bg-v3-surface-2 focus:text-v3-text lg:min-h-9 [&_svg]:size-4 [&_svg]:text-v3-text-2";
export const MENU_ITEM_DESTRUCTIVE =
  "text-v3-rec-text focus:text-v3-rec-text [&_svg]:text-v3-rec-text";
export const MENU_LABEL =
  "px-3 pb-1 pt-2 text-[12px] font-medium text-v3-text-3";
export const MENU_SEPARATOR = "mx-2 my-1 bg-v3-border";

const MOTION =
  "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";
const ROW_BASE =
  "relative flex cursor-default select-none items-center py-1.5 outline-hidden transition-colors data-disabled:pointer-events-none data-disabled:opacity-50 data-[state=open]:bg-v3-surface-2";

export const Menu = DropdownMenuPrimitive.Root;
export const MenuTrigger = DropdownMenuPrimitive.Trigger;
export const MenuGroup = DropdownMenuPrimitive.Group;
export const MenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
export const MenuSub = DropdownMenuPrimitive.Sub;

/** The menu panel, in a portal. */
export function MenuContent({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentPropsWithRef<
  typeof DropdownMenuPrimitive.Content
>): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        className={cn(
          "z-50 overflow-hidden v3-remap",
          MENU_CONTENT,
          MOTION,
          className,
        )}
        data-slot="menu-content"
        sideOffset={sideOffset}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

export function MenuItem({
  className,
  destructive = false,
  inset = false,
  ...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Item> & {
  /** Delete, sign out: the record red. */
  destructive?: boolean;
  /** Line up with radio and checkbox items. */
  inset?: boolean;
}): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.Item
      className={cn(
        ROW_BASE,
        "[&>svg]:size-4 [&>svg]:shrink-0",
        MENU_ITEM,
        inset && "pl-8",
        destructive && MENU_ITEM_DESTRUCTIVE,
        className,
      )}
      data-slot="menu-item"
      {...props}
    />
  );
}

/** A choice in a MenuRadioGroup: a dot marks the current one. */
export function MenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<
  typeof DropdownMenuPrimitive.RadioItem
>): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.RadioItem
      className={cn(ROW_BASE, MENU_ITEM, "pl-8", className)}
      data-slot="menu-radio-item"
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          {/* 8px: the item's [&_svg]:size-4 would otherwise blow the dot up. */}
          <Circle className="size-2! fill-current text-v3-text!" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
}

/** A toggle in a menu: a tick while on. */
export function MenuCheckboxItem({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<
  typeof DropdownMenuPrimitive.CheckboxItem
>): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      className={cn(ROW_BASE, MENU_ITEM, "pl-8", className)}
      data-slot="menu-checkbox-item"
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

/** A small group label inside a menu. */
export function MenuLabel({
  className,
  ...props
}: React.ComponentPropsWithRef<
  typeof DropdownMenuPrimitive.Label
>): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.Label
      className={cn("py-1.5", MENU_LABEL, className)}
      data-slot="menu-label"
      {...props}
    />
  );
}

export function MenuSeparator({
  className,
  ...props
}: React.ComponentPropsWithRef<
  typeof DropdownMenuPrimitive.Separator
>): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.Separator
      className={cn("h-px", MENU_SEPARATOR, className)}
      data-slot="menu-separator"
      {...props}
    />
  );
}

/** Opens a submenu (a chevron on the right). */
export function MenuSubTrigger({
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<
  typeof DropdownMenuPrimitive.SubTrigger
>): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.SubTrigger
      className={cn(
        ROW_BASE,
        "[&_svg]:pointer-events-none [&_svg]:shrink-0",
        MENU_ITEM,
        className,
      )}
      data-slot="menu-sub-trigger"
      {...props}
    >
      {children}
      <ChevronRight className="ml-auto" />
    </DropdownMenuPrimitive.SubTrigger>
  );
}

export function MenuSubContent({
  className,
  ...props
}: React.ComponentPropsWithRef<
  typeof DropdownMenuPrimitive.SubContent
>): React.JSX.Element {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        className={cn(
          "z-50 overflow-hidden v3-remap",
          MENU_CONTENT,
          MOTION,
          className,
        )}
        data-slot="menu-sub-content"
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}
